import { getCurrentLanguage, loadCreateCommonTranslations, resolveCreateCommonText, resolveImageGenError } from '../translations.js';
import { openFieldEditModal } from './fieldEditModal.js';
import { loadDanbooruTags, resolveAlias, isForbiddenTag, sanitizePromptTags } from './danbooruTags.js';
import { initOAuthManager } from '../utils/oauthManager.js';
import { showLoginRequired, postGenerate } from './imageGeneration.js';
import { requestAiGenerate, requestAiGenerateField, checkCharacterExists, extractFirstUrl, requestBrowsePage, resolveBrowseStoryContent, SCENARIO_BROWSE_INSTRUCTIONS } from './generationApi.js';
import { catalogNamesDiffer, replaceFallbackSuggestedNames } from '../utils/character-name-tokens.js';
import { createAiProgressController } from './aiProgressStatus.js';
import { fetchCache } from '../utils/fetchCache.js';
import { unwrapOutfitsPayload } from './utils/unwrapOutfitsPayload.js';
import { lockFormControls, unlockFormControls, addOptionIfNotExist as ensureSelectOption } from './utils/formHelpers.js';

export async function initializeAIAssistant() {
    const requestContainer = document.getElementById('ai-request-container');
    const aiRequest = document.getElementById('ai-request');
    const submitBtn = document.getElementById('submit-ai-request');
    const loading = document.getElementById('ai-loading');

    const progress = createAiProgressController(document.getElementById('ai-progress-status'));

    if (!requestContainer || !aiRequest || !submitBtn || !loading) {
        console.error('AI Assistant elements not found in DOM');
        return;
    }

    const lockScenarioForm = () => lockFormControls('scenario-form', ['.back-btn', '.visibility-btn', '#clear-draft-btn']);
    const unlockScenarioForm = () => unlockFormControls('scenario-form', ['.back-btn', '.visibility-btn', '#clear-draft-btn']);

    // ==== Robust JSON parsing helper (LLM output tolerant) ====
    // Handles:
    // - Markdown code fences (```json ... ```)
    // - Leading/trailing prose around JSON
    // - Windows CRLF (\r\n) line endings that can break fence stripping
    const extractFirstJsonValue = (text) => {
        const s = String(text || '');
        const objIdx = s.indexOf('{');
        const arrIdx = s.indexOf('[');
        const startIdx = (objIdx === -1) ? arrIdx : (arrIdx === -1 ? objIdx : Math.min(objIdx, arrIdx));
        if (startIdx === -1) return null;

        const openChar = s[startIdx];
        const closeChar = openChar === '{' ? '}' : ']';

        let depth = 0;
        let inString = false;
        let isEscaped = false;

        for (let i = startIdx; i < s.length; i++) {
            const ch = s[i];

            if (inString) {
                if (isEscaped) {
                    isEscaped = false;
                    continue;
                }
                if (ch === '\\') {
                    isEscaped = true;
                    continue;
                }
                if (ch === '"') inString = false;
                continue;
            }

            if (ch === '"') {
                inString = true;
                continue;
            }
            if (ch === openChar) depth++;
            if (ch === closeChar) {
                depth--;
                if (depth === 0) return s.slice(startIdx, i + 1);
            }
        }

        return null;
    };

    const safeParseJSON = (text) => {
        if (text === null || text === undefined) return null;

        const original = String(text);
        const normalized = original.replace(/\r\n/g, '\n').replace(/^\uFEFF/, '').trim();
        const candidates = [normalized];

        // If there's a fenced JSON block anywhere, prioritize inner contents.
        const fenceMatch = normalized.match(/```[a-zA-Z0-9_-]*\s*\n([\s\S]*?)\n```/);
        if (fenceMatch?.[1]) candidates.unshift(fenceMatch[1].trim());

        // If prose exists around JSON, extract the first complete JSON value.
        const extracted = extractFirstJsonValue(normalized);
        if (extracted) candidates.push(extracted.trim());

        for (const candidate of candidates) {
            if (!candidate) continue;
            try { return JSON.parse(candidate); } catch { /* try next */ }
        }

        return null;
    };

    // Helper function to get auth token
    const getAuthToken = async () => {
        return await window.tokenManager?.getValidAccessToken?.() ?? null;
    };

    const addOptionIfNotExist = (select, value, text) => ensureSelectOption(select, value, text, { selectValue: true });

    // Updated: Handle both image and video previews
    const setPreviewImage = (div, url) => {
        if (!div || !url) return;
        div.innerHTML = ''; // Clear existing content
        div.style.backgroundImage = ''; // Reset background

        if (url.endsWith('.mp4') || url.includes('.mp4?') || url.endsWith('.webm') || url.includes('.webm?')) { // Handle video URLs
            const video = document.createElement('video');
            video.src = url;
            video.autoplay = true;
            video.loop = true;
            video.muted = true;
            video.style.width = '100%';
            // Let banner preview shrink-wrap on mobile by using height:auto
            video.style.height = (div.id === 'banner-image-preview') ? 'auto' : '100%';
            video.style.objectFit = 'contain';
            div.appendChild(video);
        } else { // Handle images
            // Use <img> for banner preview so container can shrink to image height
            if (div.id === 'banner-image-preview') {
                const img = document.createElement('img');
                img.src = url;
                img.alt = 'Poster preview';
                img.style.width = '100%';
                img.style.height = 'auto';
                img.style.display = 'block';
                img.style.objectFit = 'contain';
                div.appendChild(img);
                return;
            }

            div.style.backgroundImage = `url(${url})`;
            div.style.backgroundSize = 'contain';
            div.style.backgroundPosition = 'center';
            div.style.backgroundRepeat = 'no-repeat';
        }
    };

    // Helper to set character status icon (only for AI assistant flow)
    // exists === true -> green check; exists === false -> red X; exists == null/'none' -> hidden (empty slot)
    const setCharStatus = (idx, exists) => {
        const el = document.getElementById(`char-status-${idx}`);
        if (!el) return;
        if (exists === null || exists === 'none') {
            el.className = 'status-icon';
            el.style.color = 'transparent';
            el.title = '';
            el.dataset.state = 'none';
            return;
        }
        el.className = `status-icon fa-solid ${exists ? 'fa-circle-check' : 'fa-circle-xmark'}`;
        el.style.color = exists ? 'var(--color-success)' : 'var(--color-error)';
        el.title = exists ? 'Existing character' : 'New character';
    };

    // caches to remember synthetic assets created during this session
    window.syntheticBackgroundPreviews = window.syntheticBackgroundPreviews || {};
    window.syntheticCharacterPreviews = window.syntheticCharacterPreviews || {};

    submitBtn.addEventListener('click', async () => {
        const oauthManager = initOAuthManager();
        if (!(await oauthManager.isAuthenticated())) {
            showLoginRequired();
            return;
        }

        const userPrompt = aiRequest.value.trim();
        if (!userPrompt) {
            const { showWarning } = await import('../utils/UnifiedNotificationSystem.js');
            showWarning('Please enter a request.', { icon: 'edit', duration: 4000 });
            return;
        }

        if (loading) {
            loading.classList.add('is-hidden');
        }

        try {
            window.scenarioWizard?.completeStep(0);
            window.scenarioWizard?.setStep(1);
        } catch (e) {
            console.warn('[AI] Failed to advance wizard to step 1:', e);
        }

        // Lock the entire form to prevent user input during AI generation
        lockScenarioForm();

        let workerResponded = false;
        try {
            // Get authentication token
            const token = await getAuthToken();
            if (!token) {
                throw new Error('Authentication required. Please log in.');
            }

            let storyContent;
            const storyUrl = extractFirstUrl(userPrompt);

            if (storyUrl) {
                progress.startBrowsePhase();
                try {
                    const browseData = await requestBrowsePage({
                        url: storyUrl,
                        instructions: SCENARIO_BROWSE_INSTRUCTIONS,
                        purpose: 'scenario',
                        token,
                        paginate: true,
                    });
                    storyContent = resolveBrowseStoryContent(browseData, userPrompt);
                } catch (browseErr) {
                    if (browseErr.message === 'QUOTA_HANDLED') throw browseErr;
                    console.warn('Page browse failed, falling back to user prompt', browseErr);
                    storyContent = userPrompt;
                }
            } else {
                storyContent = userPrompt;
            }

            // Fetch outfits for local UI lookup (outfitMap used for dropdown labels post-response).
            // Outfits data for the AI prompt is now fetched server-side from R2.
            const lang = getCurrentLanguage() || 'en';
            let availableOutfits = [];
            try {
                const data = await fetchCache.fetchOutfits(lang);
                availableOutfits = unwrapOutfitsPayload(data);
            } catch (e) {
                console.warn('Failed to fetch outfits list for local lookup, using defaults');
            }
            const outfitMap = new Map();
            availableOutfits.forEach(outfit => {
                if (outfit && outfit.id !== undefined) outfitMap.set(outfit.id, outfit.name);
            });

            const enrichedPrompt = `Generate scenario based on this extracted story content: ${storyContent}`;
            progress.startGeneratePhase({ variant: 'scenario' });
            const aiOutput = await requestAiGenerate({ prompt: enrichedPrompt, isCharacter: false, locale: lang }, token);
            if (!aiOutput || typeof aiOutput !== 'object' || aiOutput.error) {
                throw new Error(aiOutput?.error || 'Invalid AI response');
            }
            workerResponded = true;


            // Prepare scenario-level tags as vibes (not Danbooru). Keep Danbooru strictly for prompts/character descriptions.
            await loadDanbooruTags(); // still needed later for character tag normalization
            const rawVibes = Array.isArray(aiOutput.vibeTags)
                ? aiOutput.vibeTags
                : (Array.isArray(aiOutput.tags) ? aiOutput.tags : []);
            const trimmedVibes = rawVibes.map(t => String(t || '').trim()).filter(Boolean);
            const scenarioTagsUnified = trimmedVibes.slice(0, 5);

            // Auto-fill form fields
            const adventureTitle = document.getElementById('adventure-title');
            if (adventureTitle) adventureTitle.value = aiOutput.title || '';

            const scenarioDescription = document.getElementById('scenario-description');
            if (scenarioDescription) scenarioDescription.value = aiOutput.description || '';

            const adventureBannerPrompt = document.getElementById('adventure-banner-prompt');
            if (adventureBannerPrompt) adventureBannerPrompt.value = (aiOutput.bannerPrompt || '').replace(/_/g, ' ');

            // Set tag picker selection (enum-constrained)
            try {
                const { setScenarioTags } = await import('./tags/scenarioTags.js');
                setScenarioTags(scenarioTagsUnified);
            } catch (e) {
                console.warn('[AI Assistant] Failed to set scenario tags via picker', e);
            }
            // Dispatch input events so the character counters reflect the new content immediately
            ['adventure-title', 'scenario-description', 'adventure-banner-prompt'].forEach(id => {
                const el = document.getElementById(id);
                if (el) {
                    el.dispatchEvent(new Event('input', { bubbles: true }));
                }
            });

            /**
             * Banner generation should happen right after the banner prompt is produced.
             * Previously this happened at the very end (after background handling) and also
             * duplicated work (AI assistant generated a banner directly AND clicked the button).
             * We run the same generation pipeline as the button (imageGeneration.generateImage),
             * but we DO NOT depend on the click handler being attached yet.
             */
            try {
                const promptVal = adventureBannerPrompt?.value?.trim() || '';
                if (promptVal) {
                    // Defer generation slightly (scenarioForm/init may still be wiring up the UI)
                    // and retry once. This avoids "silent no-op" scenarios where the preview DOM
                    // isn't ready or was temporarily mutated during draft restore.
                    const runAutoBanner = async () => {
                        const bannerPrev = document.getElementById('banner-image-preview');
                        if (!bannerPrev) return;
                        if (bannerPrev.dataset.aiAutoGenerated) return;

                        // Mark immediately so retries don't double-fire.
                        bannerPrev.dataset.aiAutoGenerated = 'true';

                        // Keep auth handling consistent; if auth is missing, just skip banner generation
                        // (don’t abort the entire scenario AI flow).
                        try {
                            const oauthManager = initOAuthManager();
                            if (!(await oauthManager.isAuthenticated())) {
                                showLoginRequired();
                                return;
                            }
                        } catch (e) {
                            console.warn('[AI] Auth check failed for auto banner:', e);
                            return;
                        }

                        const { generateImage, setLoadingState, setPreviewLoadingState } = await import('./imageGeneration.js');
                        const bannerType = document.getElementById('ai-banner-type')?.value || 'image';

                        try {
                            setLoadingState('generate-banner-btn', true);
                            setPreviewLoadingState('banner-image-preview', true);
                            await generateImage(promptVal, 'banner', 'banner-image-preview', bannerType);
                            if (window.saveScenarioDraft) {
                                try { await window.saveScenarioDraft(); } catch (e) { console.warn('[AI] saveScenarioDraft failed after auto banner:', e); }
                            }
                        } finally {
                            setLoadingState('generate-banner-btn', false);
                            setPreviewLoadingState('banner-image-preview', false);
                        }
                    };

                    // Fire-and-forget so we don't block the AI assistant flow.
                    setTimeout(() => { runAutoBanner().catch(e => console.warn('[AI] Auto banner failed:', e)); }, 0);
                    setTimeout(() => { runAutoBanner().catch(() => { }); }, 900);
                }
            } catch (e) {
                console.warn('[AI] Failed to auto-generate banner early:', e);
            }

            // Advance to step 1 immediately after Step 1 is populated.
            // Doing this early prevents later background/banner generation from blocking the UI transition.
            try {
                if (window.scenarioWizard && typeof window.scenarioWizard.setStep === 'function') {
                    if (typeof window.scenarioWizard.completeStep === 'function') {
                        window.scenarioWizard.completeStep(0);
                    }
                    window.scenarioWizard.setStep(1);
                }
            } catch (e) {
                console.warn('[AI] Failed to advance wizard to step 1:', e);
            }

            // IMPORTANT: Don't keep the entire form locked while we do long-running asset work
            // (banner/background generation). Users should be able to edit Step 1 immediately.
            unlockScenarioForm();

            // Handle number of characters and visibility
            const characters = aiOutput.characters || [];
            const numChars = Math.min(3, Math.max(1, characters.length));
            const numCharsSelect = document.getElementById('num-characters');
            if (numCharsSelect) {
                numCharsSelect.value = numChars.toString();
                numCharsSelect.dispatchEvent(new Event('change'));
            }

            const resolvedCharacters = [];
            for (let i = 0; i < Math.min(numChars, 3); i++) {
                let char = characters[i] || { name: '', goal: '', outfit: 0 };
                if (!char.name || !char.name.trim()) char.name = `Character ${i + 1}`;

                let gender = 'female';
                if (char.gender && typeof char.gender === 'string') {
                    const g = char.gender.toLowerCase();
                    if (g === 'male' || g === 'female') gender = g;
                }

                if (!char.description || !char.description.trim()) {
                    char.description = gender === 'male' ? '1boy' : '1girl';
                }
                char.description = sanitizePromptTags(char.description, {
                    suggestionType: 'characterPrompt',
                    forbiddenCategories: [6, 7],
                }) || char.description;

                const greetingIntents = Array.isArray(char.greeting_intents)
                    ? char.greeting_intents
                    : ((char.greeting_intent || '').trim() ? [(char.greeting_intent || '').trim()] : []);

                let details = null;
                try {
                    details = await checkCharacterExists({ name: char.name, description: char.description || '' });
                } catch (charErr) {
                    console.warn('Character processing error:', charErr);
                }

                const resolvedId = details?.id != null ? String(details.id) : '';
                const suggestedName = char.name;
                const resolvedName = details?.name || char.name;
                resolvedCharacters.push({
                    origSlot: i + 1,
                    id: resolvedId,
                    suggestedName,
                    name: resolvedName,
                    fallback: !!(resolvedId && catalogNamesDiffer(suggestedName, resolvedName)),
                    gender,
                    goal: char.goal || '',
                    greetingIntents,
                    outfitId: details?.outfit ? parseInt(details.outfit, 10) : 0,
                    existsInDb: !!(details && details.id),
                    lookupFailed: !details
                });
            }

            const namesByOrigSlot = {};
            resolvedCharacters.forEach((item) => {
                namesByOrigSlot[item.origSlot] = item.name;
            });
            const rewriteText = (value) => (
                typeof window.rewriteAiCharTokensToNames === 'function'
                    ? window.rewriteAiCharTokensToNames(value, namesByOrigSlot)
                    : value
            );
            const fallbackSlots = resolvedCharacters.map((item) => ({
                slot: item.origSlot,
                suggestedName: item.suggestedName,
                fallback: item.fallback
            }));
            if (fallbackSlots.some((slot) => slot.fallback)) {
                const rewriteFallback = (value) => replaceFallbackSuggestedNames(value, fallbackSlots);
                ['adventure-title', 'scenario-description'].forEach((id) => {
                    const el = document.getElementById(id);
                    if (el) el.value = rewriteFallback(el.value || '');
                });
                resolvedCharacters.forEach((item) => {
                    item.goal = rewriteFallback(item.goal || '');
                    item.greetingIntents = (item.greetingIntents || []).map((intent) => rewriteFallback(intent));
                });
            }
            if (typeof window.applyAiCharTokensToNames === 'function') {
                window.applyAiCharTokensToNames(namesByOrigSlot);
            } else {
                ['adventure-title', 'scenario-description', 'adventure-banner-prompt'].forEach((id) => {
                    const el = document.getElementById(id);
                    if (el) el.value = rewriteText(el.value);
                });
            }
            resolvedCharacters.forEach((item) => {
                item.goal = rewriteText(item.goal);
                item.greetingIntents = (item.greetingIntents || []).map((intent) => rewriteText(intent));
            });

            const sortable = resolvedCharacters.filter((item) => item.id && /^\d+$/.test(item.id));
            const sorted = [...sortable].sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10));
            const unmatched = resolvedCharacters.filter((item) => !item.id || !/^\d+$/.test(item.id));
            const ordered = [...sorted, ...unmatched];

            if (typeof window.resetCrewIdentities === 'function') window.resetCrewIdentities();
            if (typeof window.setScenarioAiCastBatch === 'function') window.setScenarioAiCastBatch(true);
            try {
                const intentsBySlot = {};
                for (let slot = 1; slot <= 3; slot += 1) {
                    const item = ordered[slot - 1];
                    const roleSelect = document.getElementById(`crew-role-${slot}`);
                    if (!item) {
                        if (typeof window.setCrewCharacterSlot === 'function') {
                            window.setCrewCharacterSlot(slot, { id: '', name: '' }, { dispatchChange: false });
                        }
                        const goalField = document.getElementById(`message-${slot}`);
                        if (goalField) goalField.value = '';
                        setCharStatus(slot, null);
                        continue;
                    }

                    const genderSelect = document.getElementById('ai-character-gender');
                    if (genderSelect) {
                        genderSelect.value = item.gender;
                        genderSelect.dispatchEvent(new Event('change', { bubbles: true }));
                    }

                    const goalField = document.getElementById(`message-${slot}`);
                    if (goalField) {
                        goalField.value = item.goal || '';
                        goalField.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                    intentsBySlot[slot] = item.greetingIntents || [];

                    if (item.existsInDb && typeof window.setCrewCharacterSlot === 'function') {
                        window.setCrewCharacterSlot(slot, { id: item.id, name: item.name }, { dispatchChange: true });
                        addOptionIfNotExist(roleSelect, item.outfitId || 0, outfitMap.get(item.outfitId || 0) || 'default');
                        if (roleSelect) {
                            roleSelect.value = String(item.outfitId || 0);
                            roleSelect.dispatchEvent(new Event('change'));
                        }
                        setCharStatus(slot, true);
                    } else {
                        if (typeof window.setCrewCharacterSlot === 'function') {
                            window.setCrewCharacterSlot(slot, { id: '', name: '' }, { dispatchChange: true });
                        }
                        addOptionIfNotExist(roleSelect, 0, 'default');
                        setCharStatus(slot, item.lookupFailed ? false : null);
                    }
                }
                if (typeof window.setScenarioGreetingIntentsFromDraft === 'function') {
                    window.setScenarioGreetingIntentsFromDraft(intentsBySlot);
                }
            } finally {
                if (typeof window.setScenarioAiCastBatch === 'function') window.setScenarioAiCastBatch(false);
                if (typeof window.refreshVisibleCastPreviews === 'function') window.refreshVisibleCastPreviews();
            }

            /* === Handle background === */
            const timeOfDayRaw = (aiOutput.timeOfDay || '').toString().toLowerCase();
            let timeOfDayVal = '0.1';
            if (timeOfDayRaw.includes('sunset') || timeOfDayRaw.includes('2')) timeOfDayVal = '0.2';
            else if (timeOfDayRaw.includes('night') || timeOfDayRaw.includes('3')) timeOfDayVal = '0.3';
            const todSelectEl = document.getElementById('time-of-day-1');
            if (todSelectEl) todSelectEl.value = timeOfDayVal;

            // When the AI returns a location_id, set the location dropdown directly.
            const locationId = aiOutput.location_id != null ? String(aiOutput.location_id) : null;
            if (locationId) {
                const bgSelect = document.getElementById('generate-bg-1');
                if (bgSelect) {
                    bgSelect.value = locationId;
                    bgSelect.dispatchEvent(new Event('change'));
                }
            }
            /* === End background handling === */

            // Trigger input events for counters/previews
            const inputEvent = new Event('input', { bubbles: true });
            document.querySelectorAll('input, textarea, select').forEach(el => el.dispatchEvent(inputEvent));

            const { showSuccess } = await import('../utils/UnifiedNotificationSystem.js');
            showSuccess('Form auto-filled successfully! Review and edit as needed.', { icon: 'check-circle', duration: 4000 });

            // Move the user from the AI prompt step (step 0) into the editable story step (step 1).
            // scenarioForm.js exposes a wizard controller + a saveDraft helper for this exact flow.
            try {
                if (window.saveScenarioDraft) {
                    await window.saveScenarioDraft();
                }
            } catch (e) {
                console.warn('[AI] saveScenarioDraft failed after autofill:', e);
            }

        } catch (error) {
            if (!workerResponded) {
                try {
                    window.scenarioWizard?.setStep(0);
                } catch (e) {
                    console.warn('[AI] Failed to return wizard to step 0:', e);
                }
            }
            if (error.message === 'QUOTA_HANDLED') return;
            console.error('AI Assistant Error:', error);
            const { showError } = await import('../utils/UnifiedNotificationSystem.js');
            showError('Failed to generate from AI: ' + error.message, { icon: 'exclamation-circle', duration: 5000 });
        } finally {
            progress.clear();
            if (loading) {
                loading.classList.add('is-hidden');
            }

            // Unlock the form when generation is complete (success or error)
            unlockScenarioForm();
            submitBtn.disabled = false;
        }
    });

    // Skip AI assistant: advance directly to step 1 without calling the AI
    const skipBtn = document.getElementById('skip-ai-assistant');
    if (skipBtn) {
        skipBtn.addEventListener('click', () => {
            window.scenarioWizard?.completeStep(0);
            window.scenarioWizard?.setStep(1);
        });
    }

    // --- Field-mode AI helpers (single-field edits) ---
    const getScenarioContextSnapshot = () => {
        // Keep this lightweight: use story idea + a few key fields for coherence
        const parts = [];
        const storyIdea = document.getElementById('ai-request')?.value?.trim();
        if (storyIdea) parts.push(`Story idea: ${storyIdea}`);
        const title = document.getElementById('adventure-title')?.value?.trim();
        if (title) parts.push(`Title: ${title}`);
        const desc = document.getElementById('scenario-description')?.value?.trim();
        if (desc) parts.push(`Summary: ${desc}`);
        return parts.join('\n');
    };

    const getCharIdentityContext = () => {
        if (typeof window.getCharIdentitySnapshot === 'function') {
            const rows = window.getCharIdentitySnapshot() || [];
            if (rows.length) {
                return rows.map((row) => (
                    row.empty
                        ? `{char${row.letter}} is empty — keep the token {char${row.letter}}`
                        : `{char${row.letter}} is ${row.name} (id ${row.id})`
                )).join('\n');
            }
        }
        return '';
    };

    const getCastSnapshot = () => {
        const parts = [];
        const identityContext = getCharIdentityContext();
        if (identityContext) parts.push(`Identity map (always 3):\n${identityContext}`);
        const num = Math.min(3, Math.max(1, parseInt(document.getElementById('num-characters')?.value || '1', 10) || 1));
        for (let i = 1; i <= num; i++) {
            const charSel = document.getElementById(`crew-char-${i}`);
            const roleSel = document.getElementById(`crew-role-${i}`);
            const charId = charSel?.value || '';
            const charName = charSel?.options?.[charSel.selectedIndex]?.textContent?.trim() || '(empty)';
            const roleId = roleSel?.value || '';
            const roleName = roleSel?.options?.[roleSel.selectedIndex]?.textContent?.trim() || roleId;
            const goal = document.getElementById(`message-${i}`)?.value?.trim() || '';
            parts.push(`Visual slot ${i}: name=${charName}${charId ? `, id=${charId}` : ''}${roleName ? `, outfit=${roleName}` : ''}${goal ? `, goal=${goal}` : ''}`);
        }
        return parts.join('\n');
    };

    const resolveFieldKeyForElement = (el) => {
        const id = el?.id || '';
        // Map DOM ids to worker field keys (keep worker keys stable)
        const map = {
            'adventure-title': 'title',
            'scenario-description': 'description',
            'scenario-tag-search': 'tags',
            'adventure-banner-prompt': 'bannerPrompt',
            'custom-bg-textarea': 'backgroundPrompt',
            'custom-bg-name': 'backgroundName',
            'num-characters': 'numCharacters',
            'generate-bg-1': 'backgroundName',
            'time-of-day-1': 'timeOfDay',
        };
        return map[id] || id || 'description';
    };

    const getHumanFieldLabel = (targetEl) => {
        const label = targetEl?.closest('label.form-field');
        const labelSpan = label ? label.querySelector('.form-label') : null;
        if (!labelSpan) return 'this field';
        // Remove icon button text from label (if any) by reading only text nodes
        const text = Array.from(labelSpan.childNodes)
            .filter(n => n.nodeType === Node.TEXT_NODE)
            .map(n => (n.textContent || '').trim())
            .join(' ')
            .trim();
        return text || 'this field';
    };

    const WAND_ICON_HTML = '<i class="fas fa-magic" aria-hidden="true"></i>';
    const WAND_SPINNER_HTML = '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i>';

    const setWandSpinner = (wandBtn, spinning) => {
        if (!wandBtn) return;
        wandBtn.disabled = spinning;
        wandBtn.innerHTML = spinning ? WAND_SPINNER_HTML : WAND_ICON_HTML;
    };

    const requestFieldEdit = async (targetEl, wandBtn = null) => {
        if (!targetEl) return;

        const oauthManager = initOAuthManager();
        if (!(await oauthManager.isAuthenticated())) {
            showLoginRequired();
            return;
        }

        const fieldLabel = getHumanFieldLabel(targetEl);
        const instruction = await openFieldEditModal({ fieldLabel });
        if (!instruction) return;

        const token = await getAuthToken();
        if (!token) throw new Error('Authentication required. Please log in.');

        const field = resolveFieldKeyForElement(targetEl);
        let currentValue = targetEl.value || '';
        // For selects, store the visible label as currentValue (more useful to the model)
        if (targetEl && targetEl.tagName === 'SELECT') {
            const opt = targetEl.selectedOptions && targetEl.selectedOptions[0];
            if (opt && opt.textContent) currentValue = opt.textContent.trim();
        }
        const context = [
            getScenarioContextSnapshot(),
            getCastSnapshot(),
        ].filter(Boolean).join('\n\n');

        const prevDisabled = !!targetEl.disabled;
        targetEl.disabled = true;
        setWandSpinner(wandBtn, true);

        try {
            const payload = await requestAiGenerateField({ field, instruction: instruction.trim(), currentValue, context }, token);

            const nextValue = typeof payload?.value === 'string' ? payload.value.trim() : '';
            if (nextValue) {
                if (targetEl.tagName === 'SELECT') {
                    const selectEl = targetEl;
                    const selectId = selectEl.id || '';

                    if (selectId === 'num-characters') {
                        const n = Math.max(1, Math.min(3, parseInt(nextValue, 10) || 1));
                        selectEl.value = String(n);
                        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
                    } else if (selectId === 'time-of-day-1') {
                        const v = nextValue.toLowerCase();
                        const mapped =
                            v.includes('sunset') ? '0.2' :
                                v.includes('night') ? '0.3' :
                                    '0.1';
                        selectEl.value = mapped;
                        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
                    } else if (selectId === 'generate-bg-1') {
                        const desired = nextValue.toLowerCase();
                        const options = Array.from(selectEl.options || []);
                        const match =
                            options.find(o => (o.textContent || '').trim().toLowerCase() === desired) ||
                            options.find(o => (o.textContent || '').trim().toLowerCase().includes(desired)) ||
                            options.find(o => desired.split(/\s+/).filter(Boolean).every(w => (o.textContent || '').toLowerCase().includes(w)));
                        if (match && match.value) {
                            selectEl.value = match.value;
                            selectEl.dispatchEvent(new Event('change', { bubbles: true }));
                        } else {
                            throw new Error(`Couldn't find a matching setting option for: "${nextValue}"`);
                        }
                    } else {
                        // Fallback: best-effort assign
                        selectEl.value = nextValue;
                        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                } else {
                    targetEl.value = nextValue;
                    targetEl.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
            if (typeof window.resolveFilledIdentityTokensInForm === 'function') {
                window.resolveFilledIdentityTokensInForm();
            }

            const { showSuccess } = await import('../utils/UnifiedNotificationSystem.js');
            showSuccess(resolveCreateCommonText('field_edit.success_updated_field'), { icon: 'check-circle', duration: 2500 });
        } catch (e) {
            if (e.message === 'QUOTA_HANDLED') return;
            const { showError } = await import('../utils/UnifiedNotificationSystem.js');
            showError(resolveCreateCommonText('field_edit.error_field_update', { message: e.message }), { icon: 'exclamation-circle', duration: 5000 });
        } finally {
            targetEl.disabled = prevDisabled;
            setWandSpinner(wandBtn, false);
        }
    };

    const regenerateCharacterSlotFromInstruction = async (slot, instruction) => {
        const idx = parseInt(slot, 10);
        if (!idx || idx < 1 || idx > 3) return;

        const oauthManager = initOAuthManager();
        if (!(await oauthManager.isAuthenticated())) {
            showLoginRequired();
            return;
        }

        const token = await getAuthToken();
        if (!token) throw new Error('Authentication required. Please log in.');

        try { await loadDanbooruTags(); } catch { }

        const charSelect = document.getElementById(`crew-char-${idx}`);
        const roleSelect = document.getElementById(`crew-role-${idx}`);
        const goalField = document.getElementById(`message-${idx}`);

        const currentCharLabel = charSelect?.selectedOptions?.[0]?.textContent?.trim() || '';
        const currentCharId = charSelect?.value || '';
        const currentGoal = goalField?.value?.trim() || '';

        const context = [
            getScenarioContextSnapshot(),
            `Target slot: ${idx}`,
            `Current slot character id: ${currentCharId || '(none)'}`,
            `Current slot character label: ${currentCharLabel || '(none)'}`,
            `Current slot goal: ${currentGoal || '(none)'}`,
            `Current cast:\n${getCastSnapshot()}`,
        ].filter(Boolean).join('\n\n');

        const lockIds = [`crew-char-${idx}`, `crew-role-${idx}`];
        const lockEls = lockIds.map(id => document.getElementById(id)).filter(Boolean);
        const prevDisabled = new Map(lockEls.map(el => [el, !!el.disabled]));
        lockEls.forEach(el => { el.disabled = true; });

        try {
            const payload = await requestAiGenerateField({
                field: 'characters',
                instruction: `${instruction}\n\nGenerate exactly 1 character for slot ${idx}.`,
                currentValue: JSON.stringify({ slot: idx, character: currentCharLabel, goal: currentGoal }),
                context,
            }, token);

            const rawValue = typeof payload?.value === 'string' ? payload.value.trim() : '';
            const characters = rawValue ? (safeParseJSON(rawValue) || JSON.parse(rawValue)) : null;
            if (!Array.isArray(characters) || !characters.length) {
                throw new Error('AI did not return a character.');
            }

            const char = characters[0] || {};
            const name = String(char.name || `Character ${idx}`).trim();
            const gender = (String(char.gender || 'female').toLowerCase().includes('male')) ? 'male' : 'female';
            const goal = String(char.goal || '').trim();
            const description = String(char.description || '').trim() || (gender === 'male' ? '1boy' : '1girl');

            if (goalField && goal) {
                goalField.value = goal;
                goalField.dispatchEvent(new Event('input', { bubbles: true }));
            }

            let existsId = null;
            try {
                const details = await checkCharacterExists({ name, description });
                existsId = details && details.id ? String(details.id) : null;
            } catch { existsId = null; }

            if (existsId) {
                if (typeof window.setCrewCharacterSlot === 'function') {
                    window.setCrewCharacterSlot(idx, { id: existsId, name }, { dispatchChange: true });
                } else if (charSelect) {
                    charSelect.value = existsId;
                    charSelect.dispatchEvent(new Event('change'));
                }
                if (roleSelect) roleSelect.dispatchEvent(new Event('change'));
                setCharStatus(idx, true);
                const { showSuccess } = await import('../utils/UnifiedNotificationSystem.js');
                showSuccess(`Updated character slot ${idx}.`, { icon: 'check-circle', duration: 2500 });
                return;
            }

            // Character not in DB – leave dropdown empty; do not create synthetic or trigger character creation
            if (typeof window.setCrewCharacterSlot === 'function') {
                window.setCrewCharacterSlot(idx, { id: '', name: '' }, { dispatchChange: true });
            } else if (charSelect) {
                charSelect.value = '';
            }
            setCharStatus(idx, null);
            const { showSuccess } = await import('../utils/UnifiedNotificationSystem.js');
            showSuccess(`Slot ${idx} updated. Select an existing character or leave empty.`, { icon: 'check-circle', duration: 2500 });
        } catch (e) {
            if (e.message === 'QUOTA_HANDLED') return;
            const { showError } = await import('../utils/UnifiedNotificationSystem.js');
            showError(`Character regen failed: ${e.message}`, { icon: 'exclamation-circle', duration: 5500 });
        } finally {
            lockEls.forEach(el => { el.disabled = prevDisabled.get(el) || false; });
        }
    };

    const regenerateGoalsFromInstruction = async (instruction) => {
        const oauthManager = initOAuthManager();
        if (!(await oauthManager.isAuthenticated())) {
            showLoginRequired();
            return;
        }
        const token = await getAuthToken();
        if (!token) throw new Error('Authentication required. Please log in.');

        const num = Math.min(3, Math.max(1, parseInt(document.getElementById('num-characters')?.value || '1', 10) || 1));
        const inputs = [];
        for (let slot = 1; slot <= num; slot += 1) {
            inputs.push(document.getElementById(`message-${slot}`));
        }
        const currentValues = inputs.map((input) => input?.value || '');
        const context = [
            getScenarioContextSnapshot(),
            getCastSnapshot(),
            `Return exactly ${num} goal strings, aligned to visual slots 1..${num}. Use {charA}/{charB}/{charC} and {user}; never invent a fourth identity.`,
        ].filter(Boolean).join('\n\n');

        const payload = await requestAiGenerateField({
            field: 'characterGoals',
            instruction: instruction.trim(),
            currentValue: JSON.stringify(currentValues),
            context,
        }, token);

        const rawValue = typeof payload?.value === 'string' ? payload.value.trim() : '';
        let nextValues = safeParseJSON(rawValue);
        if (!Array.isArray(nextValues) && rawValue) {
            try { nextValues = JSON.parse(rawValue); } catch { nextValues = null; }
        }
        if (!Array.isArray(nextValues) || nextValues.length !== num) {
            throw new Error('AI did not return a goal for every visible character.');
        }
        inputs.forEach((input, index) => {
            if (!input) return;
            input.value = String(nextValues[index] || '').trim();
            input.dispatchEvent(new Event('input', { bubbles: true }));
        });
        if (typeof window.resolveFilledIdentityTokensInForm === 'function') {
            window.resolveFilledIdentityTokensInForm();
        }
    };

    const regenerateGreetingRowFromInstruction = async (row, instruction) => {
        if (!row) return;
        const oauthManager = initOAuthManager();
        if (!(await oauthManager.isAuthenticated())) {
            showLoginRequired();
            return;
        }
        const token = await getAuthToken();
        if (!token) throw new Error('Authentication required. Please log in.');

        const num = Math.min(3, Math.max(1, parseInt(document.getElementById('num-characters')?.value || '1', 10) || 1));
        const inputs = [];
        for (let slot = 1; slot <= num; slot += 1) {
            inputs.push(row.querySelector(`.greeting-intent-input[data-slot="${slot}"]`));
        }
        const currentValues = inputs.map((input) => input?.value || '');
        const context = [
            getScenarioContextSnapshot(),
            getCastSnapshot(),
            `Greeting variation index: ${row.dataset.variation || '0'} (1-based display ${Number(row.dataset.variation || 0) + 1}).`,
            `Return exactly ${num} greeting strings, aligned to visual slots 1..${num}. Use {charA}/{charB}/{charC} and {user}.`,
        ].filter(Boolean).join('\n\n');

        const payload = await requestAiGenerateField({
            field: 'greetingIntents',
            instruction: instruction.trim(),
            currentValue: JSON.stringify(currentValues),
            context,
        }, token);

        const rawValue = typeof payload?.value === 'string' ? payload.value.trim() : '';
        let nextValues = safeParseJSON(rawValue);
        if (!Array.isArray(nextValues) && rawValue) {
            try { nextValues = JSON.parse(rawValue); } catch { nextValues = null; }
        }
        if (!Array.isArray(nextValues) || nextValues.length !== num) {
            throw new Error('AI did not return a greeting for every character in this variation.');
        }
        inputs.forEach((input, index) => {
            if (!input) return;
            input.value = String(nextValues[index] || '').trim();
            input.dispatchEvent(new Event('input', { bubbles: true }));
        });
        if (typeof window.resolveFilledIdentityTokensInForm === 'function') {
            window.resolveFilledIdentityTokensInForm();
        }
    };

    const regenerateBackgroundFromInstruction = async (instruction) => {
        const oauthManager = initOAuthManager();
        if (!(await oauthManager.isAuthenticated())) {
            showLoginRequired();
            return;
        }

        const token = await getAuthToken();
        if (!token) throw new Error('Authentication required. Please log in.');

        const bgSelect = document.getElementById('generate-bg-1');
        const todSelect = document.getElementById('time-of-day-1');
        const previewDiv = document.getElementById('bg-preview-1');

        const currentBgLabel = bgSelect?.selectedOptions?.[0]?.textContent?.trim() || '';
        const currentTodVal = todSelect?.value || '0.1';
        const currentTod =
            currentTodVal === '0.2' ? 'sunset' :
                currentTodVal === '0.3' ? 'night' :
                    'day';

        const currentPrompt = document.getElementById('custom-bg-textarea')?.value?.trim() || '';
        const currentName = document.getElementById('custom-bg-name')?.value?.trim() || currentBgLabel.replace(/\s*\(New\)\s*$/i, '').trim();

        const context = [
            getScenarioContextSnapshot(),
            `Current background name: ${currentName || currentBgLabel || '(none)'}`,
            `Current background prompt: ${currentPrompt || '(none)'}`,
            `Current timeOfDay: ${currentTod}`,
        ].filter(Boolean).join('\n');

        // Lock relevant controls while regenerating
        const lockIds = ['generate-bg-1', 'time-of-day-1'];
        const lockEls = lockIds.map(id => document.getElementById(id)).filter(Boolean);
        const prevDisabled = new Map(lockEls.map(el => [el, !!el.disabled]));
        lockEls.forEach(el => { el.disabled = true; });

        try {
            const payload = await requestAiGenerateField({
                field: 'background',
                instruction,
                currentValue: JSON.stringify({ backgroundName: currentName, backgroundPrompt: currentPrompt, timeOfDay: currentTod }),
                context,
            }, token);

            const rawValue = typeof payload?.value === 'string' ? payload.value.trim() : '';
            const parsed = safeParseJSON(rawValue) || JSON.parse(rawValue);
            const backgroundName = (parsed?.backgroundName || '').trim() || 'Custom Background';
            const backgroundPrompt = (parsed?.backgroundPrompt || '').trim() || 'detailed background';
            const timeOfDayRaw = String(parsed?.timeOfDay || 'day').toLowerCase();
            const timeOfDayVal =
                timeOfDayRaw.includes('sunset') ? '0.2' :
                    timeOfDayRaw.includes('night') ? '0.3' :
                        '0.1';

            if (typeof window.showCustomBackgroundUI === 'function') {
                window.showCustomBackgroundUI();
            }

            const nameInput = document.getElementById('custom-bg-name');
            const area = document.getElementById('custom-bg-textarea');
            if (nameInput) {
                nameInput.classList.remove('is-hidden');
                nameInput.value = backgroundName;
            }
            if (area) {
                area.classList.remove('is-hidden');
                area.value = backgroundPrompt;
            }

            // Set time of day select (this also triggers existing preview regeneration logic)
            if (todSelect) {
                todSelect.value = timeOfDayVal;
                todSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }

            // Create/select a synthetic background option so it can be re-selected later
            const idBase = backgroundName.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 48);
            const syntheticId = `new-bg-${idBase}`;
            if (bgSelect) {
                // Remove any existing synthetic options so we only keep one
                Array.from(bgSelect.options)
                    .filter(o => o.value && o.value.startsWith('new-bg-'))
                    .forEach(o => o.remove());

                const opt = new Option(`${backgroundName} (New)`, syntheticId);
                bgSelect.insertBefore(opt, bgSelect.options[1] || null);
                bgSelect.value = syntheticId;
                bgSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }

            // Generate image for this new background
            const { response: bgRes, data: bgJson } = await postGenerate({
                prompt: backgroundPrompt,
                type: 'background',
                format: 'webp',
                parameters: {
                    negative_prompt: 'bad quality,worst quality,worst detail,sketch,censor, text, watermark, humans, people,',
                    width: 1360,
                    height: 1024
                },
                time_of_day: timeOfDayVal,
                danbooru_tags: []
            });
            if (!bgRes.ok) {
                const { showError } = await import('../utils/UnifiedNotificationSystem.js');
                const code = bgJson?.details || bgJson?.error;
                const msg = (code && resolveImageGenError(code)) || code || 'Background generation failed';
                showError(msg, { icon: 'exclamation-circle', duration: 0, closable: true });
                return;
            }
            const imageUrl = bgJson?.imageUrl || '';
            if (imageUrl && previewDiv) {
                setPreviewImage(previewDiv, imageUrl);
            }

            // Cache per-variant so time-of-day switching can reuse it
            window.syntheticBackgroundPreviews = window.syntheticBackgroundPreviews || {};
            window.syntheticBackgroundPrompts = window.syntheticBackgroundPrompts || {};
            const cacheKey = `${syntheticId}.${timeOfDayVal}`;
            if (imageUrl) window.syntheticBackgroundPreviews[cacheKey] = imageUrl;
            window.syntheticBackgroundPrompts[cacheKey] = backgroundPrompt;

            const { showSuccess } = await import('../utils/UnifiedNotificationSystem.js');
            showSuccess('Background regenerated.', { icon: 'check-circle', duration: 3000 });
        } catch (e) {
            if (e.message === 'QUOTA_HANDLED') return;
            const { showError } = await import('../utils/UnifiedNotificationSystem.js');
            showError(`Background regen failed: ${e.message}`, { icon: 'exclamation-circle', duration: 0, closable: true });
        } finally {
            lockEls.forEach(el => { el.disabled = prevDisabled.get(el) || false; });
        }
    };

    // Inject a small wand button next to each field label (skip step 0).
    // This avoids touching HTML and keeps behavior consistent across translations.
    await loadCreateCommonTranslations(getCurrentLanguage());

    const createWandButton = (targetEl) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ai-wand-btn';
        btn.setAttribute('aria-label', resolveCreateCommonText('field_edit.wand_aria'));
        btn.setAttribute('title', resolveCreateCommonText('field_edit.wand_title'));
        btn.innerHTML = WAND_ICON_HTML;

        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            requestFieldEdit(targetEl, btn);
        });

        btn.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            requestFieldEdit(targetEl, btn);
        });

        return btn;
    };

    const injectWands = (fieldIds = []) => {
        fieldIds.forEach((id) => {
            const el = document.getElementById(id);
            if (!el || el.dataset.aiWandInjected) return;

            // Skip step 0
            const stepSection = el.closest('section[data-step]');
            if (stepSection && stepSection.getAttribute('data-step') === '0') return;

            const label = el.closest('label.form-field');
            const labelSpan = label ? label.querySelector('.form-label') : null;
            if (!label || !labelSpan) return;

            label.classList.add('ai-wand-enabled');
            const wand = createWandButton(el);
            // IMPORTANT: don't insert a <button> before the input inside a <label>.
            // Otherwise the label's "associated control" becomes the button, so clicking the label text opens the modal.
            // Append the wand at the end and position it via CSS.
            label.appendChild(wand);
            el.dataset.aiWandInjected = 'true';
        });
    };

    injectWands([
        'adventure-banner-prompt',
        'scenario-description',
    ]);

    // Step 2: per-character wands (Lead/Supporting/Minor) to regenerate only that slot.
    // Note: searchDropdown.js rewires label[for="crew-char-X"] to point at the custom trigger for a11y,
    // so we can't rely on the `for=` attribute staying stable. Instead, resolve via the cast column wrapper.
    const injectCharacterSlotWands = () => {
        for (let i = 1; i <= 3; i++) {
            const castColumn = document.getElementById(`crew-char-${i}`)?.closest('.cast-column');
            const label = castColumn ? castColumn.querySelector('label.cast-label') : null;
            if (!label || label.dataset.aiCharWandInjected) continue;
            label.dataset.aiCharWandInjected = 'true';

            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'ai-wand-btn';
            btn.setAttribute('aria-label', `Regenerate character slot ${i} with AI`);
            btn.setAttribute('title', 'Regenerate with AI');
            btn.innerHTML = WAND_ICON_HTML;

            const handleClick = async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const instruction = await openFieldEditModal({ fieldLabel: `Character ${i}` });
                if (!instruction) return;
                setWandSpinner(btn, true);
                try {
                    await regenerateCharacterSlotFromInstruction(i, instruction.trim());
                } finally {
                    setWandSpinner(btn, false);
                }
            };

            btn.addEventListener('click', handleClick);
            btn.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                handleClick(e);
            });

            label.appendChild(btn);
        }
    };

    injectCharacterSlotWands();
    setTimeout(injectCharacterSlotWands, 0);

    const injectGoalRowWand = () => {
        const wrap = document.querySelector('.scenario-cast-fields');
        if (!wrap || wrap.dataset.aiWandInjected) return;
        wrap.dataset.aiWandInjected = 'true';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ai-wand-btn character-goals-wand';
        btn.setAttribute('aria-label', 'Regenerate all character goals with AI');
        btn.setAttribute('title', resolveCreateCommonText('field_edit.wand_title'));
        btn.innerHTML = WAND_ICON_HTML;

        const handleClick = async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const instruction = await openFieldEditModal({ fieldLabel: 'Character goals' });
            if (!instruction) return;
            setWandSpinner(btn, true);
            try {
                await regenerateGoalsFromInstruction(instruction.trim());
                const { showSuccess } = await import('../utils/UnifiedNotificationSystem.js');
                showSuccess(resolveCreateCommonText('field_edit.success_updated_field'), { icon: 'check-circle', duration: 2500 });
            } catch (err) {
                if (err.message === 'QUOTA_HANDLED') return;
                const { showError } = await import('../utils/UnifiedNotificationSystem.js');
                showError(`Goals regen failed: ${err.message}`, { icon: 'exclamation-circle', duration: 5500 });
            } finally {
                setWandSpinner(btn, false);
            }
        };

        btn.addEventListener('click', handleClick);
        btn.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            handleClick(e);
        });
        wrap.appendChild(btn);
    };

    injectGoalRowWand();

    const injectGreetingRowWands = () => {
        document.querySelectorAll('#greeting-intents-grid .greeting-intent-row').forEach((row) => {
            if (row.dataset.aiWandInjected) return;
            row.dataset.aiWandInjected = 'true';
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'ai-wand-btn greeting-intent-wand';
            btn.setAttribute('aria-label', 'Regenerate this greeting variation with AI');
            btn.setAttribute('title', resolveCreateCommonText('field_edit.wand_title'));
            btn.innerHTML = WAND_ICON_HTML;

            const handleClick = async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const instruction = await openFieldEditModal({ fieldLabel: 'Greeting variation' });
                if (!instruction) return;
                setWandSpinner(btn, true);
                try {
                    await regenerateGreetingRowFromInstruction(row, instruction.trim());
                    const { showSuccess } = await import('../utils/UnifiedNotificationSystem.js');
                    showSuccess(resolveCreateCommonText('field_edit.success_updated_field'), { icon: 'check-circle', duration: 2500 });
                } catch (err) {
                    if (err.message === 'QUOTA_HANDLED') return;
                    const { showError } = await import('../utils/UnifiedNotificationSystem.js');
                    showError(`Greeting regen failed: ${err.message}`, { icon: 'exclamation-circle', duration: 5500 });
                } finally {
                    setWandSpinner(btn, false);
                }
            };

            btn.addEventListener('click', handleClick);
            btn.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                handleClick(e);
            });
            row.appendChild(btn);
        });
    };

    injectGreetingRowWands();
    window.refreshScenarioGreetingWands = injectGreetingRowWands;
}
